import {
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { CapacityProgress, LimitInput, TableCard } from '../styles';
import { GroupLimit } from '../types';
import { getStatus, occupancyPct } from '../utils';

interface PoleLimitsTableProps {
  groups: GroupLimit[];
  draftLimits: Record<number, string>;
  invalidGroupIds: number[];
  onLimitChange: (criteriaGroupId: number, value: string) => void;
  onOpenWaitlist: (group: GroupLimit) => void;
}

// one row per pair (selected pole × criteria group) — the capacity unit
// since PROT-2950
export function PoleLimitsTable({
  groups,
  draftLimits,
  invalidGroupIds,
  onLimitChange,
  onOpenWaitlist,
}: PoleLimitsTableProps): JSX.Element {
  return (
    <TableCard>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Grupo de critérios</TableCell>
              <TableCell>Credenciados ativos</TableCell>
              <TableCell>Ocupação</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Limite do grupo</TableCell>
              <TableCell>Vagas restantes</TableCell>
              <TableCell>Fila de espera</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group) => {
              const draftLimit = Number(draftLimits[group.criteriaGroupId]);
              const hasError = invalidGroupIds.includes(group.criteriaGroupId);
              const displayLimit = hasError ? group.limit : draftLimit;
              // occupied slots = active accreditations + pending offers
              // (offers hold a slot until answered/expired — PROT-2955),
              // both already discounted from the backend available_slots
              const occupiedSlots = group.limit - group.availableSlots;
              const occupancy = Math.min(
                occupancyPct(occupiedSlots, displayLimit),
                100
              );
              let progressColor: 'error' | 'secondary' | 'warning' =
                'secondary';
              if (occupancy >= 100) {
                progressColor = 'error';
              } else if (occupancy >= 90) {
                progressColor = 'warning';
              }
              const status = getStatus(occupiedSlots, displayLimit);

              return (
                <TableRow key={group.criteriaGroupId}>
                  <TableCell>
                    <Typography fontWeight={600}>
                      {group.criteriaGroupName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>
                      {group.activeAccreditations}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box
                      alignItems="center"
                      display="flex"
                      gap={1.5}
                      minWidth={180}
                    >
                      <CapacityProgress
                        color={progressColor}
                        value={occupancy}
                        variant="determinate"
                      />
                      <Typography color="text.secondary" fontSize={13}>
                        {Math.round(occupancy)}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      color={status.color}
                      label={status.label}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <LimitInput
                      error={hasError}
                      helperText={
                        hasError ? `Mínimo: ${group.activeAccreditations}` : ' '
                      }
                      inputProps={{
                        'aria-label': `Limite de credenciados do grupo ${group.criteriaGroupName}`,
                        min: group.activeAccreditations,
                        step: 1,
                      }}
                      onChange={(event) =>
                        onLimitChange(group.criteriaGroupId, event.target.value)
                      }
                      size="small"
                      type="number"
                      value={draftLimits[group.criteriaGroupId]}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>
                      {Math.max(displayLimit - occupiedSlots, 0)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box alignItems="center" display="flex" gap={1}>
                      <Typography fontWeight={600}>
                        {group.waitlistCount}
                      </Typography>
                      <Button
                        onClick={() => onOpenWaitlist(group)}
                        size="small"
                        sx={{ textTransform: 'none' }}
                        variant="text"
                      >
                        Ver fila
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </TableCard>
  );
}
